import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import StarIcon from '@mui/icons-material/Star';
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import {
  ANNUAL_EXPERT_PRICES,
  annualExpertPriceLabel,
  PRICING_PLANS,
  SubscriptionPlans,
  BillingPeriod,
} from '../entity';

type PricingCardItemProps = {
  planType: SubscriptionPlans;
  active: boolean;
  disabled?: boolean;
  onSelect: (plan: SubscriptionPlans, period?: BillingPeriod) => void;
  billingPeriod?: BillingPeriod;
  currency?: 'CZK' | 'EUR';
};

const PricingCardItem = ({
  planType,
  active,
  disabled = false,
  onSelect,
  currency = 'CZK',
  billingPeriod = 'MONTHLY',
}: PricingCardItemProps) => {
  const annual = billingPeriod === 'YEARLY';
  const expert = planType === 'expert';
  const plan = PRICING_PLANS[planType];
  const monthlyPrice = (currency === 'EUR' ? plan.priceValueEur : plan.priceValue) / 100;
  const annualPrice = ANNUAL_EXPERT_PRICES[currency] / 100;
  const money = (value: number, decimals = 0) =>
    `${value.toLocaleString('cs-CZ', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })} ${currency === 'EUR' ? '€' : 'Kč'}`;
  const features = expert
    ? [
        'Vše z tarifu PRO (Basic)',
        'Online rezervace z mobilu i počítače',
        'Volné termíny a potvrzení rezervací',
        'Chytrá kalkulačka pro výdělečné ceny',
        'Věrnostní program pro stálé zákazníky',
        'Automatické SMS připomínky (placené zvlášť)',
      ]
    : [
        'Přehledný diář a historie návštěv',
        'Správa procedur vašich zákazníků',
        'Chytré vyskladňování materiálu',
        'Automatický nákupní seznam',
        'Hlídání zásob a sdílený sklad',
        'Přehled tržeb, nákladů a vyúčtování',
      ];

  return (
    <Box
      component="article"
      sx={{
        position: 'relative',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        p: { xs: 3, lg: 3.5 },
        borderRadius: 4,
        order: { xs: annual ? 0 : expert ? 2 : 1, md: annual ? 1 : expert ? 2 : 0 },
        bgcolor: 'background.paper',
        background: annual ? 'linear-gradient(145deg, #d2105a, #ad124c)' : undefined,
        color: annual ? '#fff' : 'text.primary',
        border: '1px solid',
        borderColor: active ? 'primary.main' : annual ? '#c81f5b' : 'divider',
        boxShadow: annual ? '0 16px 44px #c81f5b26' : '0 8px 30px #70254108',
      }}
    >
      <Box sx={{ minHeight: 36, mb: 1 }}>
        {annual ? (
          <Chip
            icon={<StarIcon />}
            label="NEJVÝHODNĚJŠÍ VOLBA"
            size="small"
            sx={{
              bgcolor: '#ffe22d',
              color: '#241d00',
              fontWeight: 800,
              '& .MuiChip-icon': { color: 'inherit' },
            }}
          />
        ) : active ? (
          <Chip label="Váš aktuální tarif" color="primary" size="small" variant="outlined" />
        ) : null}
      </Box>
      <Typography component="h2" variant="h5" fontWeight={800}>
        {annual ? 'EXPERT ročně' : expert ? 'EXPERT měsíčně' : 'PRO (Basic)'}
      </Typography>
      <Typography
        sx={{ mt: 1, mb: 3, minHeight: 48, color: annual ? '#ffe1ec' : 'text.secondary' }}
      >
        {annual
          ? 'Kompletní výbava. Výhodnější cena.'
          : expert
            ? 'Vše pro váš salon s měsíční platbou.'
            : 'Základ pro každodenní práci v salonu.'}
      </Typography>
      <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
        <Typography
          sx={{
            fontSize: { xs: '2.25rem', lg: '2.5rem' },
            fontWeight: 800,
            letterSpacing: '-0.04em',
          }}
        >
          {money(annual ? annualPrice / 12 : monthlyPrice, annual ? 2 : currency === 'EUR' ? 2 : 0)}
        </Typography>
        <Typography variant="body2">/ měsíc</Typography>
      </Stack>
      <Typography variant="body2" sx={{ mt: 0.5, color: annual ? '#ffe1ec' : 'text.secondary' }}>
        {annual
          ? `${annualExpertPriceLabel(currency)} za rok · platba předem`
          : 'Platba každý měsíc · bez ročního závazku'}
      </Typography>
      {annual && (
        <Box
          sx={{
            mt: 2,
            p: 1.25,
            fontSize: '.875rem',
            borderRadius: '12px',
            padding: '12px 8px',
            textAlign: 'center',
            fontWeight: '750',
            backgroundColor: '#c0f2b6',
            color: '#299916',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 800 }}>
            Ušetříte {money(monthlyPrice * 12 - annualPrice, currency === 'EUR' ? 2 : 0)} každý rok
          </Typography>
        </Box>
      )}
      <Stack
        component="ul"
        spacing={1.5}
        sx={{
          listStyle: 'none',
          p: 0,
          my: 3,
          pt: 3,
          borderTop: '1px solid',
          borderColor: annual ? '#ffffff40' : 'divider',
        }}
      >
        {features.map(feature => (
          <Stack component="li" direction="row" spacing={1} key={feature}>
            <CheckIcon
              sx={{ fontSize: 20, flexShrink: 0, color: annual ? '#fff' : 'primary.main' }}
            />
            <Typography variant="body2">{feature}</Typography>
          </Stack>
        ))}
      </Stack>
      {annual && (
        <Box
          component="div"
          className="pricing-gift"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            padding: '15px',
            background: 'white',
            borderRadius: '14px',
            color: '#111827',
            marginBottom: '16px',
            '& > svg': {
              color: '#c81f5b',
              fontSize: '32px',
              flexShrink: '0',
            },
            '& span': {
              display: 'block',
              fontSize: '10px',
              fontWeight: '800',
              letterSpacing: '0.07em',
              color: '#bd1453',
              marginBottom: '5px',
            },
            '& strong': {
              display: 'block',
              fontSize: '14px',
            },
            '& small': {
              display: 'block',
              color: '#59657c',
              marginTop: '4px',
            },
          }}
        >
          <CardGiftcardOutlinedIcon />
          <Box component="div">
            <Typography variant="body2" sx={{ fontWeight: 800 }}>
              BONUS K ROČNÍMU TARIFU
            </Typography>
            <Typography variant="body2">Import kontaktů v ceně</Typography>
            <Typography variant="body2">Běžně {currency === 'EUR' ? '28 €' : '599 Kč'}</Typography>
          </Box>
        </Box>
      )}
      <Box sx={{ mt: 'auto' }}>
        <Button
          fullWidth
          size="large"
          variant={annual ? 'contained' : 'outlined'}
          disabled={disabled || active}
          onClick={() => onSelect(planType, billingPeriod)}
          sx={{
            borderRadius: 2,
            fontWeight: 700,
            ...(annual
              ? { bgcolor: '#fff', color: '#ad124c', '&:hover': { bgcolor: '#fff0f5' } }
              : {}),
          }}
        >
          {active
            ? 'Aktuální tarif'
            : annual
              ? 'Chci výhodný roční EXPERT'
              : expert
                ? 'Zvolit měsíční EXPERT'
                : 'Zvolit PRO (Basic)'}
        </Button>
        {annual && (
          <Typography
            variant="caption"
            display="block"
            textAlign="center"
            sx={{ mt: 1.5 }}
            color={'#ffe1ec'}
          >
            Automatické obnovení můžete vypnout v profilu.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default PricingCardItem;
